import { jwtDecode } from "jwt-decode";

const OAUTH_STORAGE_KEYS = {
  state: "oauth_state",
  codeVerifier: "oauth_code_verifier",
  scope: "oauth_scope",
  returnTo: "oauth_return_to",
};

const DEFAULT_SCOPE =
  "timekeeper scheduled employee auth mdm_psc custom_reports";

const toBase64Url = (input: ArrayBuffer | Uint8Array) => {
  const source = input instanceof Uint8Array ? input : new Uint8Array(input);
  const base64 = btoa(String.fromCharCode(...source));
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

const randomString = (bytes = 32) => {
  const array = new Uint8Array(bytes);
  window.crypto.getRandomValues(array);
  return toBase64Url(array);
};

const createCodeChallenge = async (codeVerifier: string) => {
  const data = new TextEncoder().encode(codeVerifier);
  const digest = await window.crypto.subtle.digest("SHA-256", data);
  return toBase64Url(digest);
};

const buildAuthorizeUrl = (params: {
  codeChallenge: string;
  state: string;
  scope: string;
  redirectUri: string;
  clientId: string;
  authuser?: string;
}) => {
  const authorizeEndpoint = `${import.meta.env.VITE_TK_GW_DOMAIN}/v1/auth/v1/auth/authorize`;

  const query = new URLSearchParams({
    response_type: "code",
    client_id: params.clientId,
    scope: params.scope,
    redirect_uri: params.redirectUri,
    state: params.state,
    code_challenge: params.codeChallenge,
    code_challenge_method: "S256",
  });

  if (params.authuser) {
    query.append("authuser", params.authuser);
  }

  return `${authorizeEndpoint}?${query.toString()}`;
};

const clearOAuthTempStorage = () => {
  sessionStorage.removeItem(OAUTH_STORAGE_KEYS.state);
  sessionStorage.removeItem(OAUTH_STORAGE_KEYS.codeVerifier);
  sessionStorage.removeItem(OAUTH_STORAGE_KEYS.scope);
  sessionStorage.removeItem(OAUTH_STORAGE_KEYS.returnTo);
};

const getOAuthClientId = () => {
  return `${import.meta.env.VITE_CLIENT_ID || ""}`.replace(/"/g, "").trim();
};

/**
 * Inicia el flujo PKCE: genera state/verifier/challenge, guarda en sessionStorage
 * y redirige al servidor de autorización con window.location.assign.
 * No necesita React ni react-router.
 */
const startOAuthFlow = async (returnTo = "/", authuser?: string) => {
  if (!window.crypto?.subtle) {
    throw new Error("Crypto API no disponible en el navegador");
  }

  const clientId = getOAuthClientId();
  if (!clientId) {
    throw new Error("Falta VITE_CLIENT_ID");
  }

  const scope = DEFAULT_SCOPE;
  const redirectUri = `${window.location.origin}/callback`;

  const state = randomString(32);
  const codeVerifier = randomString(64);
  const codeChallenge = await createCodeChallenge(codeVerifier);

  sessionStorage.setItem(OAUTH_STORAGE_KEYS.state, state);
  sessionStorage.setItem(OAUTH_STORAGE_KEYS.codeVerifier, codeVerifier);
  sessionStorage.setItem(OAUTH_STORAGE_KEYS.scope, scope);
  sessionStorage.setItem(OAUTH_STORAGE_KEYS.returnTo, returnTo);

  window.location.assign(
    buildAuthorizeUrl({
      codeChallenge,
      state,
      scope,
      redirectUri,
      clientId,
      authuser,
    }),
  );
};

/**
 * Procesa el callback OAuth2. Valida state, intercambia code por token,
 * persiste la sesión en localStorage y redirige.
 * Diseñado para ejecutarse fuera de React (ej. en main.tsx antes del mount).
 */
const processOAuthCallback = async (): Promise<void> => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const returnedState = params.get("state");
  const oauthError = params.get("error");
  const oauthErrorDescription = params.get("error_description");

  const storedState = sessionStorage.getItem(OAUTH_STORAGE_KEYS.state);
  const codeVerifier = sessionStorage.getItem(OAUTH_STORAGE_KEYS.codeVerifier);
  const scope =
    sessionStorage.getItem(OAUTH_STORAGE_KEYS.scope) || DEFAULT_SCOPE;
  const returnTo =
    sessionStorage.getItem(OAUTH_STORAGE_KEYS.returnTo) || "/";
  const clientId = getOAuthClientId();

  clearOAuthTempStorage();

  if (oauthError) {
    throw new Error(oauthErrorDescription || oauthError);
  }
  if (!code) {
    throw new Error("No se recibio code en el callback");
  }
  if (!storedState || storedState !== returnedState) {
    throw new Error("State invalido en callback");
  }
  if (!codeVerifier) {
    throw new Error("No se encontro code_verifier");
  }
  if (!clientId) {
    throw new Error("Falta VITE_CLIENT_ID");
  }

  const endpoint = `${import.meta.env.VITE_TK_GW_DOMAIN}/v1/auth/v1/token`;
  const body = new URLSearchParams();
  body.append("grant_type", "authorization_code");
  body.append("client_id", clientId);
  body.append("scope", scope);
  body.append("code", code);
  body.append("code_verifier", codeVerifier);
  body.append("redirect_uri", `${window.location.origin}/callback`);

  const response = await fetch(endpoint, {
    method: "POST",
    body,
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(
      (err as { detail?: string }).detail ||
      "Error intercambiando code por token",
    );
  }

  const token = (await response.json()) as {
    access_token: string;
    token_type: string;
    expires_in: number;
    refresh_token?: string;
    refresh_expires_in?: number;
  };

  localStorage.setItem("access_token", token.access_token);
  localStorage.setItem("token_type", token.token_type);
  localStorage.setItem("expires_in", String(token.expires_in));
  if (token.refresh_token) {
    localStorage.setItem("refresh_token", token.refresh_token);
  }
  if (token.refresh_expires_in) {
    localStorage.setItem("refresh_expires_in", String(token.refresh_expires_in));
  }
  localStorage.setItem("authenticated", "true");
  sessionStorage.setItem("authenticated", "true");

  // JWT decode: assigned_tags, username y permissions_hex
  const decoded = jwtDecode<{
    tags?: string | string[];
    username?: string;
    user?: { username?: string };
    permissions_hex?: string;
  }>(token.access_token);

  localStorage.setItem("assigned_tags", decoded.tags?.toString() ?? "");
  localStorage.setItem(
    "username",
    decoded.username ?? decoded.user?.username ?? "",
  );

  const gwDomain = import.meta.env.VITE_TK_GW_DOMAIN;

  // Fetch all_permissions y resolver nombres desde el hex
  try {
    const permRes = await fetch(`${gwDomain}/v1/auth/v1/permission`, {
      headers: { Authorization: `Bearer ${token.access_token}` },
    });
    if (permRes.ok) {
      const permBody = await permRes.json();
      const allPermissions: { permission_id: number; name: string }[] =
        permBody.data ?? permBody;
      localStorage.setItem("all_permissions", JSON.stringify(allPermissions));
      const expire = new Date(Date.now() + 30 * 60 * 1000);
      localStorage.setItem("all_permissions_expire", expire.toISOString());
      sessionStorage.setItem("permissions_loaded", "true");

      if (decoded.permissions_hex) {
        const numero = BigInt(`0x${decoded.permissions_hex}`);
        const binary = numero.toString(2);
        const ids = [...binary]
          .map((bit, i) => (bit === "1" ? i + 1 : null))
          .filter(Boolean);
        const permissions = ids
          .map((id) => allPermissions.find((p) => p.permission_id === id)?.name)
          .filter((name): name is string => name !== undefined);
        localStorage.setItem("permissions", JSON.stringify(permissions));
      }
    }
  } catch (e) {
    console.warn("[OAuth] No se pudo cargar all_permissions", e);
  }

  // Fetch config_system para obtener login_redirect
  let finalRedirect = returnTo;
  try {
    const cfgRes = await fetch(`${gwDomain}/v1/timekeeper/config_system`, {
      headers: { Authorization: `Bearer ${token.access_token}` },
    });
    if (cfgRes.ok) {
      const cfgBody = await cfgRes.json();
      const configSystem: { key: string; value: string }[] =
        cfgBody.data ?? cfgBody;
      localStorage.setItem("config_system", JSON.stringify(configSystem));

      if (returnTo === "/") {
        const loginRedirect =
          configSystem.find((c) => c.key === "login_redirect")?.value ?? "";
        finalRedirect = `/${loginRedirect}`;
      }
    }
  } catch (e) {
    console.warn("[OAuth] No se pudo cargar config_system", e);
  }

  localStorage.setItem("history_path", JSON.stringify([finalRedirect]));

  // Limpiar el code de la URL antes de redirigir
  window.history.replaceState({}, document.title, finalRedirect);
  window.location.replace(finalRedirect);
};

export {
  OAUTH_STORAGE_KEYS,
  DEFAULT_SCOPE,
  randomString,
  createCodeChallenge,
  buildAuthorizeUrl,
  clearOAuthTempStorage,
  getOAuthClientId,
  startOAuthFlow,
  processOAuthCallback,
};
